import { initializeApp, onLog } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  clearIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
  waitForPendingWrites,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { reportWarning } from "./utils/reportError";
import { noteFirebaseLog } from "./utils/firestoreCache";

const firebaseConfig = {
  apiKey: "AIzaSyDovxG3kMz92uslavspf0McRKo1YioNf3s",
  authDomain: "lks-app-4e2fc.firebaseapp.com",
  projectId: "lks-app-4e2fc",
  storageBucket: "lks-app-4e2fc.firebasestorage.app",
  messagingSenderId: "937255570942",
  appId: "1:937255570942:web:0faebb17f54eb29bc084af",
};

const app = initializeApp(firebaseConfig);

/**
 * App Check – Firebase odmítne požadavky, které nepřijdou z této aplikace.
 *
 * Řeší spam na obnovu hesla: koncový bod Firebase Auth je veřejný, takže
 * kdokoli zná-li e-mail, může o reset požádat přímo, mimo náš formulář
 * i mimo jeho 60s prodlevu. Cooldown v UI proti tomu nepomůže.
 *
 * Site key reCAPTCHA v3 je veřejný údaj (posílá se do stránky), tajný je jen
 * secret key, který se vkládá v konzoli Firebase. Do .env se dát nedá –
 * prebuild skript .env při každém buildu přepisuje.
 *
 * Samotné nasazení nic neblokuje – jen přikládá token. Vynucování se zapíná
 * až v konzoli (Firebase → App Check), a to zvlášť pro jednotlivé služby:
 * začít Authentication, Firestore nechat zatím volný.
 */
const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6Lc5sLUtAAAAAE0Mq1qlBaGoRt7t-zm2YeKfxDIh";

if (typeof window !== "undefined" && RECAPTCHA_SITE_KEY) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Selhání App Check nesmí shodit aplikaci – dokud není zapnuté
    // vynucování, Firebase požadavky bez tokenu stále přijímá.
    console.warn("App Check se nepodařilo inicializovat:", err);
  }
}

// ✅ Persistence cache – opakované čtení stejných dat jde z IndexedDB, ne ze serveru
// Billable reads se účtují pouze při prvním načtení nebo po změně dat
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(), // podpora více tabů zároveň
  }),
});

// Pád trvalé mezipaměti na paměťovou SDK hlásí jen varováním – obrazovka
// rozhodčího pak ukládá blokujícím způsobem (utils/firestoreCache)
try {
  onLog(noteFirebaseLog, { level: "warn" });
} catch {
  // jen diagnostika – nesmí shodit start aplikace
}

export const auth = getAuth(app);
export const storage = getStorage(app);

// ─────────────────────────────────────────────────────────────
//  Vymazání cache Firestore (bezpečnost a)
// ─────────────────────────────────────────────────────────────
//
// Perzistentní cache (IndexedDB) je v prohlížeči společná pro všechny, kdo
// se na zařízení přihlásí, a nesmaže se ani ve chvíli, kdy pravidla pozdější
// čtení zamítnou. Na sdíleném PC nebo tabletu by v ní zůstalo, co stáhla
// dřívější relace – stará tabule a JudgeView četly celé profily (rok
// narození, telefon, číslo ZP), správce čte všechny profily a přihlášky.
// Proto:
//  - jednorázově po nasazení verze (a) se cache na každém zařízení vymaže
//    ještě před prvním použitím Firestore (resetFirestoreCacheOnce, index.tsx);
//  - při odhlášení se Firestore ukončí a cache vymaže (clearFirestoreCache),
//    stránka se pak načte znovu (restartApp).
// Vymazání databáze ukončí Firestore i v ostatních otevřených záložkách
// aplikace (SDK je zavře, jinak by se smazat nedala) – tyhle záložky se
// načtou znovu (reloadWhenCacheClearedElsewhere). Chyby se jen zapíšou do
// diagnostiky: úklid cache nesmí zablokovat start aplikace ani odhlášení.

/** Příznak v localStorage: cache na tomto zařízení už po verzi (a) vymazaná. */
export const CACHE_RESET_FLAG = "lks-cache-reset-a1";

/**
 * Kolikrát se jednorázové vymazání odložilo (otevřená záložka, neodeslané
 * zápisy) – jen pro diagnostiku, o vynuceném vymazání rozhoduje čas.
 */
export const CACHE_RESET_DEFERRED = "lks-cache-reset-a1-deferred";

/**
 * Čas (ms), od kterého se vymazání odkládá kvůli jiné záložce (záznam
 * firestore_clients_…): první takový odklad, po posunu hodin dozadu znovu.
 */
export const CACHE_RESET_CLIENTS_SINCE = "lks-cache-reset-a1-clients-since";

/**
 * Čas (ms) prvního odkladu kvůli neodeslaným zápisům (firestore_mutations_…),
 * jen pro diagnostiku – kvůli zápisům se vymazání nikdy nevynutí. Spuštění,
 * při kterém žádné nečekají, ho smaže.
 */
export const CACHE_RESET_MUTATIONS_SINCE = "lks-cache-reset-a1-mutations-since";

/**
 * Čas (ms) posledního spuštění, při kterém se vymazání odložilo – podle něj
 * se pozná, že hodiny zařízení šly dozadu (viz CACHE_RESET_CLIENTS_MAX_MS).
 */
export const CACHE_RESET_LAST_START = "lks-cache-reset-a1-last-start";

/** Odhlášení/vymazání v jedné záložce → ostatní záložky se načtou znovu (storage event). */
export const CACHE_CLEARED_SIGNAL = "lks-cache-cleared";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Jak dlouho nejvýš se vymazání odkládá kvůli jiné záložce. Její záznam
 * firestore_clients_… zůstane navždy i po záložce, která skončila bez
 * pagehide (Android zabije aplikaci, spadne prohlížeč, vypne se počítač) –
 * SDK cizí záznamy neuklízí, takže se odkládání musí jednou skončit. Limit
 * je časový, ne počtem spuštění: v den závodu se aplikace spouští mnohokrát
 * za sebou a vynucené vymazání by ukončilo Firestore v opravdu otevřené
 * záložce (stará tabule zamrzne).
 *
 * Čas měří hodiny zařízení a do lhůty se počítá i doba, kdy aplikace
 * neběžela: tabule otevíraná jen v den závodu (14 dní od sebe, přes zimu
 * měsíce) vymaže cache při prvním spuštění po 7 dnech – i bez přihlášení,
 * kde ji nevymaže odhlášení. Skok hodin dopředu se od takové pauzy rozlišit
 * nedá, počítá se proto taky (nejhůř se vymaže vynuceně hned). Lhůtu začne
 * znovu jen skok o víc než 7 dní DOZADU (vybitý tablet, ruční nastavení):
 * naměřený odklad pak neplatí a raději se odkládá déle. Neodeslané zápisy
 * se tímhle limitem neřídí – kvůli nim se vymazání nevynutí nikdy.
 */
export const CACHE_RESET_CLIENTS_MAX_MS = 7 * DAY_MS;

/** Jak dlouho nejvýš čeká první vykreslení aplikace na vymazání cache. */
const RESET_MAX_WAIT_MS = 1000;

/** Jak dlouho nejvýš čeká odhlášení na ukončení Firestore a vymazání cache. */
const CLEAR_MAX_WAIT_MS = 2000;

/** Jak dlouho čekat na odeslání rozpracovaných zápisů před odhlášením. */
const PENDING_WRITES_WAIT_MS = 3000;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** localStorage, nebo null (zakázané úložiště – už samotný getter vyhodí). */
const browserStorage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

/**
 * Kdo cache Firestore ještě používá: klient s více záložkami má
 * v localStorage záznam firestore_clients_… (smaže ho při zavření stránky –
 * po zabité aplikaci ale zůstane), neodeslané zápisy firestore_mutations_…
 * (smažou se po potvrzení serverem). Vymazání by jinou záložku ukončilo,
 * resp. zápisy zahodilo.
 */
function cacheUsers(store: Storage): { clients: boolean; mutations: boolean } {
  let clients = false;
  let mutations = false;
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i) || "";
    if (key.indexOf("firestore_clients_") === 0) clients = true;
    if (key.indexOf("firestore_mutations_") === 0) mutations = true;
  }
  return { clients, mutations };
}

/**
 * Trvá odklad už aspoň maxMs? Začátek lhůty je v localStorage pod
 * `sinceKey` – chybí-li (první odklad, nebo ho smazal skok hodin dozadu),
 * je nečitelný nebo v budoucnosti (hodiny šly dozadu), zapíše se teď:
 * raději odkládat déle než vymazat dřív.
 */
function deferralExpired(store: Storage, sinceKey: string, maxMs: number, now: number): boolean {
  const since = Number(store.getItem(sinceKey));
  if (!Number.isFinite(since) || since <= 0 || since > now) {
    store.setItem(sinceKey, String(now));
    return false;
  }
  return now - since >= maxMs;
}

/**
 * Šly hodiny od posledního spuštění s odkladem o víc než lhůtu jiné záložky
 * DOZADU? Chybí-li záznam o něm, poslouží začátek lhůty (i ten zapsalo
 * spuštění); bez obojího (první odklad) ne. Skok dopředu se nepočítá – od
 * pauzy mezi spuštěními se rozlišit nedá a patří do lhůty (jinak by ji
 * tabule spouštěná jednou za 14 dní začínala pořád znovu a cache by se
 * nevymazala nikdy).
 */
function clockWentBack(store: Storage, now: number): boolean {
  const last = Number(
    store.getItem(CACHE_RESET_LAST_START) || store.getItem(CACHE_RESET_CLIENTS_SINCE)
  );
  if (!Number.isFinite(last) || last <= 0) return false;
  return last - now > CACHE_RESET_CLIENTS_MAX_MS;
}

/** Odklad na příští spuštění: počet odkladů (diagnostika) a čas tohoto spuštění. */
function deferReset(store: Storage, now: number): Promise<void> {
  const deferred = parseInt(store.getItem(CACHE_RESET_DEFERRED) || "0", 10) || 0;
  store.setItem(CACHE_RESET_DEFERRED, String(deferred + 1));
  store.setItem(CACHE_RESET_LAST_START, String(now));
  return Promise.resolve();
}

function markCacheReset(store: Storage | null): void {
  if (!store) return;
  try {
    store.setItem(CACHE_RESET_FLAG, new Date().toISOString());
    store.removeItem(CACHE_RESET_DEFERRED);
    store.removeItem(CACHE_RESET_CLIENTS_SINCE);
    store.removeItem(CACHE_RESET_MUTATIONS_SINCE);
    store.removeItem(CACHE_RESET_LAST_START);
  } catch {
    // Nezapamatuje se – vymaže se při dalším spuštění znovu (neškodné).
  }
}

/** Ostatní záložky aplikace se načtou znovu (jejich Firestore vymazání ukončilo). */
function signalCacheCleared(store: Storage | null): void {
  if (!store) return;
  try {
    store.setItem(CACHE_CLEARED_SIGNAL, String(Date.now()));
  } catch {
    // Ostatní záložky zůstanou bez Firestore, dokud je uživatel neobnoví.
  }
}

/**
 * Jednorázové vymazání cache Firestore po nasazení verze (a). Volá se PŘED
 * vykreslením aplikace (index.tsx) – jakmile Firestore běží, vymazání
 * odmítne (failed-precondition) a zkusí se při dalším spuštění. Vrátí se
 * nejpozději po maxWaitMs; delší mazání doběhne na pozadí a čtení aplikace
 * na něj počkají (stejná fronta Firestore). Nikdy nevyhodí.
 *
 * Firestore v jiné otevřené záložce SDK při mazání neodmítne, ale ukončí
 * (stará verze aplikace by zamrzla, např. tabule) a neodeslané zápisy by se
 * ztratily – vymazání se proto odloží na příští spuštění:
 *  - neodeslané zápisy (např. výsledky rozhodčího bez signálu): pokaždé, bez
 *    časového limitu – jestli je aplikace mezitím vůbec mohla odeslat, se
 *    nepozná (start bez signálu, měsíc bez spuštění, posunuté hodiny).
 *    Odešlou se, jakmile bude signál, SDK pak jejich klíč smaže a cache se
 *    vymaže při některém z dalších spuštění. Na sdíleném zařízení ji mezitím
 *    vymaže odhlášení (počká na odeslání zápisů, jinak se zeptá);
 *  - jiná záložka (i zastaralý záznam zabité aplikace): nejvýš
 *    CACHE_RESET_CLIENTS_MAX_MS od prvního odkladu podle hodin zařízení
 *    (počítá se i doba, kdy aplikace neběžela), pak vynuceně.
 */
export function resetFirestoreCacheOnce(maxWaitMs: number = RESET_MAX_WAIT_MS): Promise<void> {
  const store = browserStorage();
  if (!store) {
    // Bez localStorage si vymazání nejde zapamatovat a IndexedDB pak
    // obvykle nefunguje taky (Firestore jede bez perzistentní cache).
    return Promise.resolve();
  }
  // Vymazání se vynutilo – záznam jiné záložky trval příliš dlouho (diagnostika).
  let forced = false;
  try {
    if (store.getItem(CACHE_RESET_FLAG)) return Promise.resolve();
    const now = Date.now();
    const users = cacheUsers(store);
    // Hodiny šly dozadu: lhůta jiné záložky začne znovu (i když teď
    // odkládají zápisy – počítala by se pak od špatného času).
    if (clockWentBack(store, now)) store.removeItem(CACHE_RESET_CLIENTS_SINCE);
    if (users.mutations) {
      // Diagnostika jen jednou, dokud zápisy čekají: od kdy vymazání brzdí.
      if (!store.getItem(CACHE_RESET_MUTATIONS_SINCE)) {
        store.setItem(CACHE_RESET_MUTATIONS_SINCE, String(now));
        reportWarning(
          "firebase.resetCache.deferred",
          "čekají neodeslané zápisy – vymazání cache počká, až se odešlou"
        );
      }
      return deferReset(store, now);
    }
    store.removeItem(CACHE_RESET_MUTATIONS_SINCE);
    if (users.clients) {
      if (!deferralExpired(store, CACHE_RESET_CLIENTS_SINCE, CACHE_RESET_CLIENTS_MAX_MS, now)) {
        return deferReset(store, now);
      }
      forced = true;
    }
  } catch (err) {
    reportWarning("firebase.resetCache.storage", err);
    return Promise.resolve();
  }

  let clearing: Promise<void>;
  try {
    clearing = clearIndexedDbPersistence(db);
  } catch (err) {
    // failed-precondition: Firestore už běží – zkusí se při dalším spuštění.
    reportWarning("firebase.resetCache", err);
    return Promise.resolve();
  }
  const done = clearing.then(
    () => {
      markCacheReset(store);
      // Vymazání ukončilo Firestore v otevřených záložkách – vynuceně, nebo
      // v záložce, která se rozběhla mezi kontrolou a mazáním (dvě záložky
      // otevřené naráz). Nové záložky se na signál načtou znovu.
      signalCacheCleared(store);
      if (forced) {
        reportWarning("firebase.resetCache.forced", "záznam jiné záložky trval příliš dlouho");
      }
    },
    (err) => reportWarning("firebase.resetCache", err)
  );
  return Promise.race([done, wait(maxWaitMs)]);
}

/**
 * Po odhlášení: ukončí Firestore a vymaže jeho cache (sdílené zařízení),
 * ostatním záložkám dá vědět, ať se načtou znovu. Čeká nejvýš maxWaitMs,
 * nikdy nevyhodí. Instance db je pak nepoužitelná – volající hned načte
 * stránku znovu (window.location).
 */
export async function clearFirestoreCache(maxWaitMs: number = CLEAR_MAX_WAIT_MS): Promise<void> {
  const store = browserStorage();
  const work = (async () => {
    await terminate(db);
    await clearIndexedDbPersistence(db);
    markCacheReset(store);
  })().catch((err) => reportWarning("firebase.clearCache", err));
  await Promise.race([work, wait(maxWaitMs)]);
  signalCacheCleared(store);
}

/**
 * Po clearFirestoreCache: stránku načte znovu na `path` (ukončený Firestore
 * se jinak nerozběhne). Offline by nové načtení skončilo chybovou stránkou
 * prohlížeče (aplikace nemá service worker) – pak se načte až po obnovení
 * spojení; do té doby zůstává zobrazené přihlášení (to bez sítě stejně
 * nefunguje).
 */
export function restartApp(
  path: string,
  go: (target: string) => void = (target) => window.location.replace(target)
): void {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    window.addEventListener("online", () => go(path), { once: true });
    return;
  }
  go(path);
}

/**
 * Odeslal Firestore všechny zápisy přihlášeného uživatele (offline fronta)?
 * false = do maxWaitMs se neodeslaly – vymazání cache při odhlášení by je
 * zahodilo (např. výsledky zapsané rozhodčím bez signálu). Nikdy nevyhodí.
 */
export async function pendingWritesSettled(
  maxWaitMs: number = PENDING_WRITES_WAIT_MS
): Promise<boolean> {
  let settled: Promise<boolean>;
  try {
    settled = waitForPendingWrites(db).then(
      () => true,
      // Odmítnuté čekání (změna uživatele, ukončený Firestore) – nic neblokovat.
      () => true
    );
  } catch {
    return true;
  }
  return Promise.race([settled, wait(maxWaitMs).then(() => false)]);
}

/**
 * Jiná záložka se odhlásila (nebo poprvé vymazala cache) – Firestore této
 * záložky tím skončil, proto se stránka načte znovu. Volá index.tsx; vrací
 * funkci pro odebrání posluchače.
 */
export function reloadWhenCacheClearedElsewhere(
  reload: () => void = () => window.location.reload()
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key === CACHE_CLEARED_SIGNAL && event.newValue) reload();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
